import { IPortfolioPerformanceHistorical } from './portfolio-performance.types';

export interface DailyStats {
  sharpe: number | null;
  sortino: number | null;
  maxDrawdownPct: number | null;
  positiveDays: number;
  negativeDays: number;
  avgWin: number | null;
  avgLoss: number | null;
  // 95% VaR/CVaR (5% tail): the 5th-percentile daily return, and the mean of
  // the days at or beyond it. Needs a reasonable sample to mean anything —
  // null below MIN_TAIL_SAMPLE days rather than reporting a 1-2-day "tail".
  var95: number | null;
  cvar95: number | null;
  // Number of daily-return observations behind these stats. Consumers use it
  // to show "Not enough data" for the sample-hungry ratios on freshly-opened
  // accounts (see MIN_RATIO_SAMPLE) rather than a meaningless annualized value.
  sampleSize: number;
}

const EMPTY_STATS: DailyStats = {
  sharpe: null,
  sortino: null,
  maxDrawdownPct: null,
  positiveDays: 0,
  negativeDays: 0,
  avgWin: null,
  avgLoss: null,
  var95: null,
  cvar95: null,
  sampleSize: 0,
};

const MIN_TAIL_SAMPLE = 20;

// Annualized Sharpe/Sortino off a handful of days is noise — a brand-new
// account with 1-2 daily points produces wild ratios. Below this many daily
// returns the ratios are suppressed (null) and the UI shows "Not enough data".
export const MIN_RATIO_SAMPLE = 10;

// Crypto trades 24/7 — annualize off calendar days, not trading days.
const ANNUALIZATION_DAYS = 365;

const MONTH_ORDER = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

// The PV on the first day of whatever window `historical` covers — used to
// show a "starting capital for this view" figure that matches the window
// actually on screen (e.g. 2026-only), rather than the account's true
// inception capital from whenever it originally opened.
export const getFirstDayPv = (
  historical: IPortfolioPerformanceHistorical | null | undefined,
): number | null => {
  if (!historical) return null;

  for (const year of Object.keys(historical).sort()) {
    const yearData = historical[year];
    for (const month of MONTH_ORDER) {
      const monthData = yearData.months[month];
      if (!monthData) continue;

      const days = Object.keys(monthData.days).sort((a, b) => Number(a) - Number(b));
      for (const day of days) {
        const pv = monthData.days[day]?.pv;
        if (typeof pv === 'number') return pv;
      }
    }
  }

  return null;
};

// The PV on the LAST closed day with a recorded pv — the anchor for
// projecting today's running PV (lastClosePv * (1 + live daily_return))
// before the backend finalizes today's own entry. See
// portfolio-performance-account-card/index.tsx.
export const getLastDayPv = (
  historical: IPortfolioPerformanceHistorical | null | undefined,
): number | null => {
  if (!historical) return null;

  for (const year of Object.keys(historical).sort().reverse()) {
    const yearData = historical[year];
    for (const month of [...MONTH_ORDER].reverse()) {
      const monthData = yearData.months[month];
      if (!monthData) continue;

      const days = Object.keys(monthData.days).sort((a, b) => Number(b) - Number(a));
      for (const day of days) {
        const pv = monthData.days[day]?.pv;
        if (typeof pv === 'number') return pv;
      }
    }
  }

  return null;
};

// Flattens to chronological order explicitly — object key insertion order
// isn't a safe substitute for date order once more than one year is present.
const flattenDailyReturnsChronological = (
  historical: IPortfolioPerformanceHistorical,
): number[] => {
  const returns: number[] = [];

  for (const year of Object.keys(historical).sort()) {
    const yearData = historical[year];
    for (const month of MONTH_ORDER) {
      const monthData = yearData.months[month];
      if (!monthData) continue;

      for (const day of Object.keys(monthData.days).sort((a, b) => Number(a) - Number(b))) {
        const dayReturn = monthData.days[day]?.return;
        if (typeof dayReturn === 'number') returns.push(dayReturn);
      }
    }
  }

  return returns;
};

// Sharpe/Sortino assume a 0% risk-free rate (standard simplification for
// crypto vol funds where there's no natural cash benchmark rate to net out).
export const computeDailyStats = (
  historical: IPortfolioPerformanceHistorical | null | undefined,
): DailyStats => {
  if (!historical) return EMPTY_STATS;

  const returns = flattenDailyReturnsChronological(historical);
  if (returns.length < 2) return { ...EMPTY_STATS, sampleSize: returns.length };

  // Suppress the annualized ratios until there's a meaningful sample; drawdown
  // and win/loss counts below still report from day one (they're not misleading
  // with a small sample the way an annualized Sharpe is).
  const hasRatioSample = returns.length >= MIN_RATIO_SAMPLE;

  const mean = returns.reduce((sum, r) => sum + r, 0) / returns.length;
  const variance = returns.reduce((sum, r) => sum + (r - mean) ** 2, 0) / returns.length;
  const stdDev = Math.sqrt(variance);
  const sharpe =
    hasRatioSample && stdDev > 0 ? (mean / stdDev) * Math.sqrt(ANNUALIZATION_DAYS) : null;

  const downsideReturns = returns.filter((r) => r < 0);
  const downsideVariance =
    downsideReturns.length > 0
      ? downsideReturns.reduce((sum, r) => sum + r ** 2, 0) / downsideReturns.length
      : 0;
  const downsideDev = Math.sqrt(downsideVariance);
  const sortino =
    hasRatioSample && downsideDev > 0 ? (mean / downsideDev) * Math.sqrt(ANNUALIZATION_DAYS) : null;

  let cumulative = 1;
  let peak = 1;
  let maxDrawdownPct = 0;
  for (const r of returns) {
    cumulative *= 1 + r;
    if (cumulative > peak) peak = cumulative;
    const drawdown = (cumulative - peak) / peak;
    if (drawdown < maxDrawdownPct) maxDrawdownPct = drawdown;
  }

  const positiveReturns = returns.filter((r) => r > 0);
  const negativeReturns = returns.filter((r) => r < 0);
  const avgWin =
    positiveReturns.length > 0
      ? positiveReturns.reduce((sum, r) => sum + r, 0) / positiveReturns.length
      : null;
  const avgLoss =
    negativeReturns.length > 0
      ? negativeReturns.reduce((sum, r) => sum + r, 0) / negativeReturns.length
      : null;

  let var95: number | null = null;
  let cvar95: number | null = null;
  if (returns.length >= MIN_TAIL_SAMPLE) {
    const sorted = [...returns].sort((a, b) => a - b);
    const tailCount = Math.max(1, Math.floor(0.05 * sorted.length));
    const tail = sorted.slice(0, tailCount);
    var95 = tail[tail.length - 1];
    cvar95 = tail.reduce((sum, r) => sum + r, 0) / tail.length;
  }

  return {
    sharpe,
    sortino,
    maxDrawdownPct,
    positiveDays: positiveReturns.length,
    negativeDays: negativeReturns.length,
    avgWin,
    avgLoss,
    var95,
    cvar95,
    sampleSize: returns.length,
  };
};
